import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram } from "lucide-react";
import { toast } from "sonner";
import { api, LOGO_WIDE } from "../lib/api";

export const Footer = ({ instagram }) => {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/subscribe", { email });
      toast.success(data.already ? "You're already on the list" : "You're in — watch your inbox");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <footer className="border-t border-white/10 bg-ink" data-testid="site-footer">
      {/* NEWSLETTER */}
      <div className="px-5 sm:px-8 py-12 border-b border-white/10" data-testid="newsletter-section">
        <div className="max-w-xl">
          <p className="text-[10px] font-bold tracking-[0.3em] text-pink mb-3">THE MONTHLY DISPATCH</p>
          <p className="font-display text-3xl sm:text-4xl tracking-wide leading-none">GET THE WORK FIRST<span className="text-pink">.</span></p>
          <p className="mt-3 text-sm text-smoke leading-relaxed">
            New artwork, print drops, upcoming events and special invitations — once a month, straight to your inbox. No spam.
          </p>
          <form onSubmit={subscribe} className="mt-5 flex" data-testid="newsletter-form">
            <label htmlFor="newsletter-email" className="sr-only">Email address</label>
            <input
              id="newsletter-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="YOUR EMAIL"
              data-testid="newsletter-email"
              className="flex-1 min-w-0 h-12 bg-charcoal border border-white/15 border-r-0 px-4 text-sm outline-none focus:border-pink placeholder:text-smoke/50 placeholder:tracking-[0.15em]"
            />
            <button type="submit" disabled={sending} data-testid="newsletter-submit"
              className="h-12 px-6 bg-bone text-ink text-xs font-extrabold tracking-[0.2em] hover:bg-pink hover:text-white transition-colors duration-200 disabled:opacity-50 shrink-0">
              {sending ? "…" : "SUBSCRIBE"}
            </button>
          </form>
        </div>
      </div>

      <div className="px-5 sm:px-8 py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-10">
          <div>
            <img src={LOGO_WIDE} alt="ARTILLERY KDP" className="h-10 w-auto mb-5" draggable={false} />
            <p className="text-smoke text-sm max-w-xs leading-relaxed">
              Original works, signed limited editions and custom murals by artist Kenny DePrince.
            </p>
          </div>
          <nav className="grid grid-cols-2 gap-x-12 gap-y-3 text-sm" data-testid="footer-nav">
            {[["ART", "/art"], ["STORE", "/prints"], ["MURALS", "/murals"], ["EVENTS", "/events"], ["CONTACT", "/contact"]].map(([label, to]) => (
              <Link key={to} to={to} data-testid={`footer-${label.toLowerCase()}`} className="text-bone/80 hover:text-pink transition-colors duration-200 tracking-[0.15em] font-semibold py-1">
                {label}
              </Link>
            ))}
          </nav>
          <div className="flex flex-col gap-1">
            <a
              href={instagram || "https://instagram.com/swingkidd"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              className="flex items-center gap-2 text-sm text-bone/80 hover:text-pink transition-colors duration-200 min-h-[44px]"
            >
              <Instagram size={18} /> FOLLOW @SWINGKIDD
            </a>
            <a href="/?intro=1" data-testid="footer-replay-intro" className="text-[10px] tracking-[0.25em] text-smoke hover:text-pink transition-colors duration-200 min-h-[44px] flex items-center">
              REPLAY INTRO
            </a>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-white/10 space-y-2">
          <p className="text-xs text-smoke" data-testid="footer-copyright">© 2026 Kenny DePrince / ARTILLERY KDP. All rights reserved.</p>
          <p className="text-[11px] text-smoke/70 leading-relaxed max-w-2xl">
            All artwork and images are protected by copyright. Reproduction, distribution, modification or commercial use without written permission is prohibited.
          </p>
        </div>
      </div>
    </footer>
  );
};
